import { useState } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { atlas, type FileResponse } from './lib/api';
import { AppShell } from './components/AppShell';
import { DocumentView, ProvenancePanel } from './components/DocumentView';
import { KnowledgePage } from './components/KnowledgePage';
import { AuthorityPanel } from './components/AuthorityPanel';
import { RevisionEditor } from './components/RevisionEditor';

/**
 * Routes.
 *
 * `/files/{path}` is the canonical document URL and survives every reclassification,
 * because it is derived from source identity rather than from where a document
 * currently appears in the tree. `?rev=` pins it to one immutable revision.
 */

const rootRoute = createRootRoute({ component: () => <AppShell /> });

function ErrorState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="error-state-view">
      <div className="error-icon" aria-hidden="true">
        📄
      </div>
      <h2>{title}</h2>
      <p>{detail}</p>
      <p className="error-meta">Check the link, or return to the catalog.</p>
    </div>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: function Welcome() {
    const generation = useQuery({ queryKey: ['generation'], queryFn: () => atlas.generation() });
    return (
      <div className="reading-container">
        <header className="doc-header">
          <h1>Alphea Atlas</h1>
        </header>
        <article className="markdown-body">
          <p>
            A read projection over committed <code>alphea-ai-memory</code> content. Browse by tag on the
            left, or search knowledge from the upper-left field.
          </p>
          {generation.data?.generation ? (
            <p className="text-small">
              Active generation <code>{generation.data.generation.generationId}</code> at revision{' '}
              <code>{generation.data.generation.revision}</code>.
            </p>
          ) : (
            <p className="text-small">No validated content generation is active yet.</p>
          )}
        </article>
      </div>
    );
  },
});

const browseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/browse',
  validateSearch: (search: Record<string, unknown>) => ({
    tags: typeof search['tags'] === 'string' ? search['tags'] : '',
    sort: typeof search['sort'] === 'string' ? search['sort'] : 'title',
  }),
  component: function Browse() {
    const { tags, sort } = useSearch({ from: browseRoute.id });
    const tagIds = tags ? tags.split(',').filter(Boolean) : [];
    const documents = useQuery({
      queryKey: ['documents', tagIds, sort],
      queryFn: () => atlas.documents(tagIds, sort),
    });

    return (
      <div className="reading-container">
        <header className="doc-header">
          <h1>Browse</h1>
          {documents.data ? <p className="text-small">Match: {documents.data.filter.description}</p> : null}
        </header>
        <article className="markdown-body">
          {documents.isLoading ? <p>Loading…</p> : null}
          {documents.data?.documents.length === 0 ? <p>No documents matched these tags.</p> : null}
          <ul className="neighbor-list">
            {documents.data?.documents.map((document) => (
              <li key={document.documentId}>
                <a href={document.url}>{document.title}</a>
                <span className="relation">{document.sourcePath}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>
    );
  },
});

const filesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/files/$',
  validateSearch: (search: Record<string, unknown>) => ({
    rev: typeof search['rev'] === 'string' ? search['rev'] : undefined,
  }),
  component: function FilePage() {
    const params = useParams({ from: filesRoute.id }) as { _splat?: string };
    const { rev } = useSearch({ from: filesRoute.id });
    const path = params._splat ?? '';
    const [editing, setEditing] = useState(false);

    const file = useQuery<FileResponse>({
      queryKey: ['file', path, rev ?? 'latest'],
      queryFn: () => atlas.file(path, rev),
      retry: false,
    });

    if (file.isLoading) return <div className="reading-container">Loading document…</div>;
    if (file.error || !file.data) {
      // Absent and not-disclosed are reported identically, exactly as the API does.
      return <ErrorState title="Document not found" detail="This document does not exist at this path or revision." />;
    }

    const dark = document.documentElement.dataset['theme'] === 'dark';

    return (
      <>
        {editing ? (
          <div className="reading-container">
            {/* The editor reads its base from the response; it never invents source. */}
            <RevisionEditor file={file.data} onClose={() => setEditing(false)} />
          </div>
        ) : null}
        <DocumentView file={file.data} dark={dark} />
        {!editing && file.data.document.isLatest && file.data.editBase ? (
          <div className="reading-container" style={{ paddingTop: 0 }}>
            <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
              Propose a revision
            </button>
          </div>
        ) : null}
      </>
    );
  },
});

const knowledgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/knowledge/$knowledgeId',
  component: function Knowledge() {
    const { knowledgeId } = useParams({ from: knowledgeRoute.id });
    const detail = useQuery({
      queryKey: ['knowledge', knowledgeId],
      queryFn: () => atlas.knowledge(knowledgeId),
      retry: false,
    });

    if (detail.isLoading) return <div className="reading-container">Loading knowledge…</div>;
    if (detail.error || !detail.data) {
      return <ErrorState title="Knowledge not found" detail="No admitted entity matches this identifier." />;
    }
    return <KnowledgePage detail={detail.data} />;
  },
});

const authorityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/authority',
  component: function Authority() {
    const authority = useQuery({ queryKey: ['authority'], queryFn: () => atlas.authority(), retry: false });
    if (authority.isLoading) return <div className="reading-container">Loading identities…</div>;
    if (!authority.data) return <ErrorState title="Unavailable" detail="Identity information is unavailable." />;
    return <AuthorityPanel authority={authority.data} />;
  },
});

const proposalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proposals/$proposalId',
  component: function Proposal() {
    const { proposalId } = useParams({ from: proposalRoute.id });
    const diff = useQuery({
      queryKey: ['proposal-diff', proposalId],
      queryFn: () => atlas.proposalDiff(proposalId),
      retry: false,
    });

    if (diff.isLoading) return <div className="reading-container">Loading proposal…</div>;
    if (!diff.data) return <ErrorState title="Proposal not found" detail="This proposal does not exist." />;

    return (
      <div className="reading-container">
        <header className="doc-header">
          <h1>Proposed revision</h1>
          <div className="doc-meta">
            <span className={`badge ${diff.data.proposal.status === 'conflict' ? 'status-error' : 'status-latest'}`}>
              {diff.data.proposal.status}
            </span>
            <span className="meta-item">{diff.data.proposal.sourcePath}</span>
          </div>
          {diff.data.parents.length > 0 ? (
            <div className="meta-row" style={{ marginTop: 8 }}>
              <span className="meta-label" style={{ color: 'var(--status-error)' }}>
                Conflict parents:
              </span>
              {diff.data.parents.map((parent) => (
                <code key={parent} className="link-path">
                  {parent}
                </code>
              ))}
            </div>
          ) : null}
        </header>
        <article className="markdown-body">
          <div className="proposal-diff">
            {diff.data.hunks.map((hunk, index) => (
              <div
                key={`${hunk.kind}:${index}`}
                className={`diff-line${hunk.kind === 'added' ? ' diff-add' : hunk.kind === 'removed' ? ' diff-del' : ''}`}
              >
                {hunk.kind === 'added' ? '+ ' : hunk.kind === 'removed' ? '- ' : '  '}
                {hunk.line}
              </div>
            ))}
          </div>
        </article>
      </div>
    );
  },
});

/** `/items/{item-id}` is an alias; it never replaces the canonical file URL. */
const itemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/items/$itemId',
  component: function ItemAlias() {
    const { itemId } = useParams({ from: itemRoute.id });
    const detail = useQuery({
      queryKey: ['knowledge', `kn:item:item-${itemId}`],
      queryFn: () => atlas.knowledge(`kn:item:item-${itemId}`),
      retry: false,
    });
    if (detail.isLoading) return <div className="reading-container">Loading item…</div>;
    if (!detail.data) return <ErrorState title="Item not found" detail="No admitted item matches this identifier." />;
    return <KnowledgePage detail={detail.data} />;
  },
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  browseRoute,
  filesRoute,
  knowledgeRoute,
  authorityRoute,
  proposalRoute,
  itemRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: false });

export { filesRoute, knowledgeRoute, ProvenancePanel };
